import DOMPurify from 'dompurify';
import * as React from 'react';
import textile from 'textile-js';
import { classes } from 'typestyle';
import { FocusVisibleIndicator } from '../../styles/FocusIndicators';
import { ElemAttrs } from '../../utils/HtmlAttrs';

export const TextFormatterMinWidth = 340;
export const TextFormatterHeight = 200;
export const DefaultAllowedTags = Object.freeze<Array<string>>([
  'a', 'abbr', 'b', 'blockquote', 'br', 'caption', 'cite', 'code', 'dd', 'del', 'div', 'dl', 'dt',
  'em', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'hr', 'i', 'img', 'ins', 'li', 'nl', 'ol', 'p', 'pre', 'span',
  'strike', 'strong', 'sub', 'sup', 'table', 'tbody', 'td', 'th', 'thead', 'tr', 'u', 'ul',
]);

const TextFormatterDisplayTestIdSuffix = '-TextFormatterDisplayWidget';

/* Set up sanitize options for use with textile */
const defaultOptions: DOMPurify.Config = {
  ALLOWED_TAGS: [...DefaultAllowedTags],
};

function convertMarkupToSanitizedHtml(markup: string, config?: DOMPurify.Config): { clean_FOR_FRONT_END_ONLY: string } {
  const dirty = textile(markup);

  // open all links in new tab
  DOMPurify.addHook('afterSanitizeAttributes', (node: Element) => {
    if (node.tagName === 'A' && node.hasAttribute('href')) {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener');
    }
  });

  return { clean_FOR_FRONT_END_ONLY: DOMPurify.sanitize(dirty, { ...defaultOptions, ...config }) as string };
}

export type TextFormatterDisplayProps = {
  /**
   * An array of all lowercase tagnames, which will be allowed to be rendered as HTML by this widget.
   * Any tag not included will not be rendered.
   */
  allowedTags?: string[];
  /** Markup to render as HTML */
  markup: string;
  /** Adds the suffix `'-TextFormatterDisplayWidget'` */
  testId?: string;
};

export const TextFormatterDisplayWidget: React.FC<TextFormatterDisplayProps & ElemAttrs> = props => {
  const { allowedTags, markup, testId, ...htmlAttrs } = props;
  const { clean_FOR_FRONT_END_ONLY } = React.useMemo(
    () => convertMarkupToSanitizedHtml(markup, (allowedTags && { ALLOWED_TAGS: [...allowedTags] })),
    [markup, allowedTags]
  );

  return (
    <div
      data-tb-test-id={testId + TextFormatterDisplayTestIdSuffix}
      {...htmlAttrs}
      className={classes(htmlAttrs.className, FocusVisibleIndicator)}
      dangerouslySetInnerHTML={{ __html: clean_FOR_FRONT_END_ONLY }}
      tabIndex={0}
    />
  );
};
TextFormatterDisplayWidget.displayName = 'TextFormatterDisplayWidget';
